import { supabase } from '@/integrations/supabase/client';
import type { ModeloCarregador } from './carregadoresComissaoService';

export interface ValoresPropostaGlobal {
  id: string;
  modelo_carregador: ModeloCarregador;
  preco_equipamento: number;
  preco_instalacao: number;
  preco_materiais: number;
  created_at: string;
  updated_at: string;
}

export interface ValoresPropostaParceiro {
  id: string;
  parceiro_id: string;
  parceiro_nome?: string;
  parceiro_pai_nome?: string | null;
  modelo_carregador: ModeloCarregador;
  preco_equipamento: number;
  preco_instalacao: number;
  preco_materiais: number;
  created_at: string;
  updated_at: string;
}

const TABLE_GLOBAL = 'carregadores_valores_propostas_globais';
const TABLE_PARCEIRO = 'carregadores_valores_propostas_parceiro';

/**
 * Lista todos os valores padrão globais de propostas por modelo.
 * Ordena na ordem: 7kW, 11kW, 22kW
 */
export async function listarValoresPropostasGlobais(): Promise<ValoresPropostaGlobal[]> {
  const { data, error } = await supabase
    .from(TABLE_GLOBAL)
    .select('*');

  if (error) throw error;
  
  const valores = (data ?? []).map((v: any) => ({
    id: v.id,
    modelo_carregador: v.modelo_carregador,
    preco_equipamento: Number(v.preco_equipamento) || 0,
    preco_instalacao: Number(v.preco_instalacao) || 0,
    preco_materiais: Number(v.preco_materiais) || 0,
    created_at: v.created_at,
    updated_at: v.updated_at,
  }));
  
  // Ordenar: modelos padrão primeiro (7kW, 11kW, 22kW), depois alfabeticamente
  const ordemModelos: Record<string, number> = {
    'Wallbox 7kW': 1,
    'Wallbox 11kW': 2,
    'Wallbox 22kW': 3,
  };
  
  return valores.sort((a, b) => {
    const ordemA = ordemModelos[a.modelo_carregador] ?? 999;
    const ordemB = ordemModelos[b.modelo_carregador] ?? 999;
    if (ordemA !== 999 || ordemB !== 999) {
      return ordemA - ordemB;
    }
    // Se ambos são modelos customizados, ordenar alfabeticamente
    return a.modelo_carregador.localeCompare(b.modelo_carregador);
  });
}

/**
 * Cria um novo modelo de valores padrão globais de propostas.
 * Apenas admin pode chamar.
 * Os triggers do banco propagarão automaticamente para todos os parceiros.
 */
export async function criarNovoModeloValoresPropostasGlobais(
  modelo: string,
  precoEquipamento: number,
  precoInstalacao: number,
  precoMateriais: number
): Promise<ValoresPropostaGlobal> {
  // Verificar se o modelo já existe
  const { data: existing } = await supabase
    .from(TABLE_GLOBAL)
    .select('id')
    .eq('modelo_carregador', modelo)
    .maybeSingle();

  if (existing?.id) {
    throw new Error(`Modelo "${modelo}" já existe`);
  }

  const valores = {
    modelo_carregador: modelo.trim(),
    preco_equipamento: Math.max(0, precoEquipamento),
    preco_instalacao: Math.max(0, precoInstalacao),
    preco_materiais: Math.max(0, precoMateriais),
  };

  const { data, error } = await supabase
    .from(TABLE_GLOBAL)
    .insert(valores)
    .select()
    .single();
    
  if (error) throw error;
  
  return {
    id: data.id,
    modelo_carregador: data.modelo_carregador,
    preco_equipamento: Number(data.preco_equipamento) || 0,
    preco_instalacao: Number(data.preco_instalacao) || 0,
    preco_materiais: Number(data.preco_materiais) || 0,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}

/**
 * Atualiza os valores padrão globais de propostas para um modelo específico.
 * Apenas admin pode chamar.
 * Se o modelo não existir, cria um novo.
 */
export async function atualizarValoresPropostasGlobais(
  modelo: ModeloCarregador,
  precoEquipamento: number,
  precoInstalacao: number,
  precoMateriais: number
): Promise<ValoresPropostaGlobal> {
  const { data: existing } = await supabase
    .from(TABLE_GLOBAL)
    .select('id')
    .eq('modelo_carregador', modelo)
    .maybeSingle();

  const valores = {
    preco_equipamento: Math.max(0, precoEquipamento),
    preco_instalacao: Math.max(0, precoInstalacao),
    preco_materiais: Math.max(0, precoMateriais),
  };

  if (existing?.id) {
    const { data, error } = await supabase
      .from(TABLE_GLOBAL)
      .update(valores)
      .eq('id', existing.id)
      .select()
      .single();
    if (error) throw error;
    return {
      id: data.id,
      modelo_carregador: data.modelo_carregador,
      preco_equipamento: Number(data.preco_equipamento) || 0,
      preco_instalacao: Number(data.preco_instalacao) || 0,
      preco_materiais: Number(data.preco_materiais) || 0,
      created_at: data.created_at,
      updated_at: data.updated_at,
    };
  }

  // Se não existe, criar novo (mas usar função específica para isso)
  const { data, error } = await supabase
    .from(TABLE_GLOBAL)
    .insert({
      modelo_carregador: modelo,
      ...valores,
    })
    .select()
    .single();
  if (error) throw error;
  return {
    id: data.id,
    modelo_carregador: data.modelo_carregador,
    preco_equipamento: Number(data.preco_equipamento) || 0,
    preco_instalacao: Number(data.preco_instalacao) || 0,
    preco_materiais: Number(data.preco_materiais) || 0,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}

/**
 * Remove um modelo de valores padrão globais.
 * Apenas admin pode chamar.
 */
export async function removerModeloValoresPropostasGlobais(
  modelo: ModeloCarregador
): Promise<void> {
  const { error } = await supabase
    .from(TABLE_GLOBAL)
    .delete()
    .eq('modelo_carregador', modelo);
    
  if (error) throw error;
}

/**
 * Lista todos os parceiros com seus valores de propostas configurados.
 * Retorna uma lista completa: todos os parceiros com os 3 modelos, mesmo que não tenham valores configurados.
 * Apenas admin pode chamar.
 */
export async function listarValoresPropostasPorParceiro(): Promise<ValoresPropostaParceiro[]> {
  // Primeiro, buscar todos os parceiros (com nome do parceiro pai quando houver)
  const { data: parceiros, error: errParceiros } = await supabase
    .from('profiles')
    .select('id, nome, pai:parceiro_cadastrado_por(nome)')
    .eq('role', 'parceiro')
    .order('nome');

  if (errParceiros) throw errParceiros;

  // Buscar valores configurados
  const { data: valoresConfigurados, error: errValores } = await supabase
    .from(TABLE_PARCEIRO)
    .select('*')
    .order('parceiro_id')
    .order('modelo_carregador');

  if (errValores) throw errValores;

  // Criar um mapa de valores por parceiro e modelo
  const valoresMap = new Map<string, Map<string, any>>();
  (valoresConfigurados ?? []).forEach((v: any) => {
    if (!valoresMap.has(v.parceiro_id)) {
      valoresMap.set(v.parceiro_id, new Map());
    }
    valoresMap.get(v.parceiro_id)!.set(v.modelo_carregador, v);
  });

  // Buscar todos os modelos disponíveis dinamicamente
  const { data: modelosGlobais, error: errModelos } = await supabase
    .from(TABLE_GLOBAL)
    .select('modelo_carregador')
    .order('modelo_carregador');
    
  if (errModelos) throw errModelos;
  
  const ordemModelos: Record<string, number> = {
    'Wallbox 7kW': 1,
    'Wallbox 11kW': 2,
    'Wallbox 22kW': 3,
  };
  const modelos: ModeloCarregador[] = (modelosGlobais ?? [])
    .map((m: any) => m.modelo_carregador)
    .sort((a, b) => {
      const ordemA = ordemModelos[a] ?? 999;
      const ordemB = ordemModelos[b] ?? 999;
      if (ordemA !== 999 || ordemB !== 999) return ordemA - ordemB;
      return a.localeCompare(b);
    });
  const resultado: ValoresPropostaParceiro[] = [];

  (parceiros ?? []).forEach((parceiro: any) => {
    const parceiroPaiNome = parceiro.pai?.nome ?? null;
    modelos.forEach((modelo) => {
      const valorConfigurado = valoresMap.get(parceiro.id)?.get(modelo);
      resultado.push({
        id: valorConfigurado?.id || '',
        parceiro_id: parceiro.id,
        parceiro_nome: parceiro.nome ?? 'Sem nome',
        parceiro_pai_nome: parceiroPaiNome,
        modelo_carregador: modelo,
        preco_equipamento: valorConfigurado ? Number(valorConfigurado.preco_equipamento) || 0 : 0,
        preco_instalacao: valorConfigurado ? Number(valorConfigurado.preco_instalacao) || 0 : 0,
        preco_materiais: valorConfigurado ? Number(valorConfigurado.preco_materiais) || 0 : 0,
        created_at: valorConfigurado?.created_at || '',
        updated_at: valorConfigurado?.updated_at || '',
      });
    });
  });

  return resultado;
}

/**
 * Lista valores padrão de propostas de um parceiro (todos os modelos).
 */
export async function listarValoresPropostasParceiroPorParceiro(
  parceiroId: string
): Promise<ValoresPropostaParceiro[]> {
  const { data, error } = await supabase
    .from(TABLE_PARCEIRO)
    .select("*")
    .eq("parceiro_id", parceiroId);

  if (error) throw error;

  return (data ?? []).map((v: {
    id: string;
    parceiro_id: string;
    modelo_carregador: ModeloCarregador;
    preco_equipamento: number;
    preco_instalacao: number;
    preco_materiais: number;
    created_at: string;
    updated_at: string;
  }) => ({
    id: v.id,
    parceiro_id: v.parceiro_id,
    modelo_carregador: v.modelo_carregador,
    preco_equipamento: Number(v.preco_equipamento) || 0,
    preco_instalacao: Number(v.preco_instalacao) || 0,
    preco_materiais: Number(v.preco_materiais) || 0,
    created_at: v.created_at,
    updated_at: v.updated_at,
  }));
}

/**
 * Obtém valores padrão de propostas para um parceiro específico e modelo.
 * Retorna null se não houver configuração específica.
 */
export async function obterValoresPropostasParceiro(
  parceiroId: string,
  modelo: ModeloCarregador
): Promise<ValoresPropostaParceiro | null> {
  const { data, error } = await supabase
    .from(TABLE_PARCEIRO)
    .select(`
      *,
      parceiro:parceiro_id (
        nome
      )
    `)
    .eq('parceiro_id', parceiroId)
    .eq('modelo_carregador', modelo)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  return {
    id: data.id,
    parceiro_id: data.parceiro_id,
    parceiro_nome: data.parceiro?.nome ?? 'Sem nome',
    modelo_carregador: data.modelo_carregador,
    preco_equipamento: Number(data.preco_equipamento) || 0,
    preco_instalacao: Number(data.preco_instalacao) || 0,
    preco_materiais: Number(data.preco_materiais) || 0,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}

/**
 * Atualiza valores padrão de propostas para um parceiro e modelo específico.
 * Apenas admin pode chamar.
 * Usa UPSERT para criar ou atualizar automaticamente.
 */
export async function atualizarValoresPropostasParceiro(
  parceiroId: string,
  modelo: ModeloCarregador,
  precoEquipamento: number,
  precoInstalacao: number,
  precoMateriais: number
): Promise<ValoresPropostaParceiro> {
  const valores = {
    preco_equipamento: Math.max(0, precoEquipamento),
    preco_instalacao: Math.max(0, precoInstalacao),
    preco_materiais: Math.max(0, precoMateriais),
  };

  // Usar UPSERT para criar ou atualizar automaticamente
  const { data, error } = await supabase
    .from(TABLE_PARCEIRO)
    .upsert(
      {
        parceiro_id: parceiroId,
        modelo_carregador: modelo,
        ...valores,
      },
      {
        onConflict: 'parceiro_id,modelo_carregador',
      }
    )
    .select(`
      *,
      parceiro:parceiro_id (
        nome
      )
    `)
    .single();
    
  if (error) throw error;
  
  return {
    id: data.id,
    parceiro_id: data.parceiro_id,
    parceiro_nome: data.parceiro?.nome ?? 'Sem nome',
    modelo_carregador: data.modelo_carregador,
    preco_equipamento: Number(data.preco_equipamento) || 0,
    preco_instalacao: Number(data.preco_instalacao) || 0,
    preco_materiais: Number(data.preco_materiais) || 0,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}
